// LaserCutInterlockingPlanar2.js - Full Enhanced Version
import React, {
  useEffect,
  useRef,
  useState,
  useCallback,
  useMemo,
} from "react";
import * as THREE from "three";
import { STLLoader } from "three/examples/jsm/loaders/STLLoader";
import { sliceModelXZ, sliceModelYZ } from "../utils/sliceModel";
import { initializeThreeScene } from "../utils/initializeThreeScene";
import { Brush, Evaluator, ADDITION, SUBTRACTION } from "three-bvh-csg";
import useVisualHelpers from "../hooks/useVisualHelpers";
import TutorialWindow from "../components/TutorialWindow";
import { laserCutInterlockingTutorial } from "../utils/tutorialContents";
import { useSyncedCamera } from "../hooks/useSyncedCamera";
import { downloadSVG } from "../utils/exportUtils";
import { normalizeModel } from "../utils/normalizeModel";
import { MACHINE_PRESETS } from "../components/Sidebar";
import "../styles/common.css";
// const LASER_CUTTER_PRESETS = {
//   'Glowforge Basic': { width: 279, height: 495 },
//   'Epilog Zing 16': { width: 406, height: 305 },
//   'Trotec Speedy 400': { width: 1016, height: 610 },
//   'Custom': { width: 10, height: 10 }
// };
const getSelectedLaserPreset = () => {
  const selected =
    window.labConstraints?.machineSettings?.laserCutter?.selected ||
    "Trotec Speedy 400";
  const bed =
    MACHINE_PRESETS.laserCutter[selected] ||
    MACHINE_PRESETS.laserCutter["Trotec Speedy 400"];
  return { selected, bed };
};
const calculateIntersections = (
  horizontalContours,
  verticalContours,
  materialThickness
) => {
  const result = { lines: [], slots: { horizontal: [], vertical: [] } };
  horizontalContours.forEach((hContour, hIndex) => {
    verticalContours.forEach((vContour, vIndex) => {
      const hY = hContour.y;
      const vX = vContour.x;
      const verticalPoints = vContour.points;
      const yCoords = verticalPoints.map((p) => p.x);
      const minY = Math.min(...yCoords);
      const maxY = Math.max(...yCoords);
      if (minY <= hY && hY <= maxY) {
        const projectedPoints = [];
        for (let i = 0; i < verticalPoints.length; i++) {
          const curr = verticalPoints[i];
          const next = verticalPoints[(i + 1) % verticalPoints.length];
          if ((curr.x - hY) * (next.x - hY) > 0) continue;
          const t = (hY - curr.x) / (next.x - curr.x);
          const z = curr.y + t * (next.y - curr.y);
          projectedPoints.push(new THREE.Vector2(vX, z));
        }
        if (projectedPoints.length >= 2) {
          projectedPoints.sort((a, b) => a.y - b.y);
          const [firstPoint, lastPoint] = [
            projectedPoints[0],
            projectedPoints.at(-1),
          ];
          const direction = new THREE.Vector2()
            .subVectors(lastPoint, firstPoint)
            .normalize();
          const offset = direction
            .clone()
            .multiplyScalar(materialThickness * 0.5);
          const start = new THREE.Vector2().copy(firstPoint).sub(offset);
          const end = new THREE.Vector2().copy(lastPoint).add(offset);
          const lineStart = new THREE.Vector3(vX, hY, start.y);
          const lineEnd = new THREE.Vector3(vX, hY, end.y);
          result.lines.push({ start: lineStart, end: lineEnd, hIndex, vIndex });
          const mid = new THREE.Vector3()
            .addVectors(lineStart, lineEnd)
            .multiplyScalar(0.5);
          const hSlot = {
            center: new THREE.Vector3()
              .addVectors(lineStart, mid)
              .multiplyScalar(0.5),
            length: lineStart.distanceTo(mid),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 0, 1),
            hIndex,
            vIndex,
          };
          const vSlot = {
            center: new THREE.Vector3()
              .addVectors(mid, lineEnd)
              .multiplyScalar(0.5),
            length: mid.distanceTo(lineEnd),
            width: materialThickness * 1.1,
            height: materialThickness * 1.1,
            direction: new THREE.Vector3(0, 1, 0),
            hIndex,
            vIndex,
          };
          result.slots.horizontal.push(hSlot);
          result.slots.vertical.push(vSlot);
        }
      }
    });
  });
  return result;
};

const applySlotsToParts = (
  horizontalPlanes,
  verticalPlanes,
  slots,
  materialThickness
) => {
  const evaluator = new Evaluator();
  const apply = (planes, slotList, isVertical) => {
    planes.forEach((plane, index) => {
      const planeSlots = slotList.filter(
        (s) => (isVertical ? s.vIndex : s.hIndex) === index
      );
      if (!planeSlots.length) return;
      const planeBrush = new Brush(plane.geometry);
      planeBrush.position.copy(plane.position);
      planeBrush.rotation.copy(plane.rotation);
      planeBrush.scale.copy(plane.scale);
      planeBrush.updateMatrixWorld(true);
      const slotBrushes = planeSlots.map((slot) => {
        const box = new THREE.BoxGeometry(
          isVertical ? slot.width : slot.width,
          isVertical ? slot.length : slot.height,
          isVertical ? slot.height : slot.length
        );
        const b = new Brush(box);
        b.position.copy(slot.center);
        b.lookAt(new THREE.Vector3().addVectors(slot.center, slot.direction));
        b.updateMatrixWorld(true);
        return b;
      });
      let combined = slotBrushes[0];
      for (let i = 1; i < slotBrushes.length; i++) {
        combined = evaluator.evaluate(combined, slotBrushes[i], ADDITION);
      }
      const resultEval = evaluator.evaluate(planeBrush, combined, SUBTRACTION);
      const inverseMatrix = new THREE.Matrix4()
        .copy(plane.matrixWorld)
        .invert();
      resultEval.geometry.applyMatrix4(inverseMatrix);
      plane.geometry.dispose();
      plane.geometry = resultEval.geometry;
    });
  };
  apply(horizontalPlanes, slots.horizontal, false);
  apply(verticalPlanes, slots.vertical, true);
};

const LaserCutInterlockingPlanar2 = ({ stlFile }) => {
  const containerRef = useRef(null);
  const sceneRef = useRef(null);
  const rendererRef = useRef(null);
  const cameraRef = useRef(null);
  const controlsRef = useRef(null);
  const modelRef = useRef(null);

  const verticalPlanes = useRef([]);
  const horizontalPlanes = useRef([]);
  const verticalContours = useRef([]);
  const horizontalContours = useRef([]);

  const { createVisualization } = useVisualHelpers();
  const [tutorialVisible, setTutorialVisible] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [cutNumber, setCutNumber] = useState(6);
  const [svgReady, setSvgReady] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [constraintWarnings, setConstraintWarnings] = useState([]);
  const [laserCutterSettings, setLaserCutterSettings] = useState({
    selected: "Trotec Speedy 400", // 修改这里
    expanded: false,
  });
  const [estimatedCutTimeSec, setEstimatedCutTimeSec] = useState(null);
  const [pathStats, setPathStats] = useState({ mmTotal: 0, contours: 0 });
  // 添加材料相关状态
  const [materialKey, setMaterialKey] = useState("wood");
  // ⬇️ put these near the top of the component body, before any usage
  const getBaseThickness = () => {
    const b = window.labConstraints?.materialBaseThickness ?? 3;
    // clamp to [1, 12]
    return Math.min(12, Math.max(1, Number(b) || 3));
  };

  // define the state FIRST (nothing above should reference materialThickness)
  const [materialThickness, setMaterialThickness] = React.useState(() =>
    getBaseThickness()
  );

  // derived slider props (these can come immediately after)
  const baseT = getBaseThickness(); // current base thickness from Sidebar
  const thicknessMin = baseT; // slider min
  const thicknessMax = 12; // slider max
  const thicknessStep = baseT; // snap step = base

  // react to Sidebar updates and re-snap the value
  React.useEffect(() => {
    const onLabUpdated = () => {
      const b = getBaseThickness();
      setMaterialThickness((prev) => {
        const snapped = Math.round((Number(prev) || b) / b) * b;
        return Math.min(12, Math.max(b, snapped));
      });
    };
    window.addEventListener("labConstraints:updated", onLabUpdated);
    return () =>
      window.removeEventListener("labConstraints:updated", onLabUpdated);
  }, []);
  // 定义材料
  const woodMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0xcd853f,
        side: THREE.DoubleSide,
      }),
    []
  );

  const acrylicMaterial = useMemo(
    () =>
      new THREE.MeshStandardMaterial({
        color: 0xffffff,
        transparent: true,
        opacity: 0.4,
        side: THREE.DoubleSide,
        shininess: 80,
      }),
    []
  );

  const currentMaterialRef = useRef(woodMaterial);
  useEffect(() => {
    currentMaterialRef.current =
      materialKey === "acrylic" ? acrylicMaterial : woodMaterial;
  }, [materialKey, woodMaterial, acrylicMaterial]);

  const pageId = useMemo(() => `interlocking-planar-2`, []);
  useSyncedCamera(pageId, cameraRef, controlsRef);
  const formatMm = (mm) => `${Math.round(mm)} mm`;
  const formatTimeS = (s) => {
    if (s == null) return "-";
    const m = Math.floor(s / 60);
    const sec = Math.round(s % 60);
    return `${m}m ${sec}s`;
  };
  const applyMaterialToPlanes = useCallback((mat) => {
    [...horizontalPlanes.current, ...verticalPlanes.current].forEach(
      (plane) => {
        plane.material = mat;
        plane.material.needsUpdate = true;
      }
    );
  }, []);
  const estimateLaserTime = useCallback(() => {
    // Heuristic constants — tweak if you like
    const CUT_SPEED_MM_S = 25; // typical for ~3mm plywood (rough)
    const TRAVEL_SPEED_MM_S = 150; // fast non-cut moves
    const PIERCE_SEC = 0.15; // start per contour
    const FIXED_OVERHEAD_SEC = 15; // focus/air assist/etc.

    const sumPerimeter = (contours) => {
      let total = 0;
      for (const c of contours) {
        const pts = c.points;
        if (!pts || pts.length < 2) continue;
        for (let i = 1; i < pts.length; i++) {
          total += pts[i].distanceTo(pts[i - 1]);
        }
        // close loop if needed
        if (!pts[0].equals(pts[pts.length - 1])) {
          total += pts[0].distanceTo(pts[pts.length - 1]);
        }
      }
      return total;
    };

    const mmHorizontal = sumPerimeter(horizontalContours.current);
    const mmVertical = sumPerimeter(verticalContours.current);
    const mmTotal = mmHorizontal + mmVertical;

    const numContours =
      (horizontalContours.current?.length || 0) +
      (verticalContours.current?.length || 0);

    // Simple time model:
    // total = cutting + travel(≈12% of cut length) + pierce per contour + fixed overhead
    const cutTime = mmTotal / CUT_SPEED_MM_S;
    const travelTime = (mmTotal * 0.12) / TRAVEL_SPEED_MM_S;
    const pierceTime = numContours * PIERCE_SEC;
    const total = cutTime + travelTime + pierceTime + FIXED_OVERHEAD_SEC;

    setPathStats({ mmTotal, contours: numContours });
    setEstimatedCutTimeSec(total);
  }, []);
  const buildClosedCurveWorld = (segments, uniqueVertices, matrixWorld) => {
    const worldVertices = uniqueVertices.map((v) =>
      v.clone().applyMatrix4(matrixWorld)
    );
    const used = new Set();
    const curve = [];
    let current = segments[0].i1;
    curve.push(worldVertices[current]);
    let found = true;
    let safety = 0;
    while (found && safety < segments.length * 2) {
      found = false;
      for (let i = 0; i < segments.length; i++) {
        if (used.has(i)) continue;
        const s = segments[i];
        if (s.i1 === current) {
          current = s.i2;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        } else if (s.i2 === current) {
          current = s.i1;
          curve.push(worldVertices[current]);
          used.add(i);
          found = true;
          break;
        }
      }
      safety++;
    }
    if (!curve[0].equals(curve[curve.length - 1])) curve.push(curve[0].clone());
    return curve;
  };

  const generateSlices = useCallback(() => {
    if (!modelRef.current || !sceneRef.current) return;
    verticalPlanes.current = [];
    horizontalPlanes.current = [];
    verticalContours.current = [];
    horizontalContours.current = [];

    const model = modelRef.current;
    model.updateMatrixWorld(true);
    const bbox = new THREE.Box3().setFromObject(model);

    const verticalX = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.x + (i / (cutNumber + 1)) * (bbox.max.x - bbox.min.x)
    );

    const horizontalY = Array.from(
      { length: cutNumber + 2 },
      (_, i) => bbox.min.y + (i / (cutNumber + 1)) * (bbox.max.y - bbox.min.y)
    );

    verticalX.forEach((x, i) => {
      const result = sliceModelYZ(model, null, x);
      if (!result?.componentSegments) return;
      result.componentSegments.forEach((segs, j) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          result.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.y, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            ...currentMaterialRef.current,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.y = Math.PI / 2;
        mesh.rotation.x = Math.PI / 2;
        mesh.position.x = x - materialThickness / 2;
        mesh.updateMatrixWorld(true);
        sceneRef.current.add(mesh);
        verticalPlanes.current.push(mesh);
        verticalContours.current.push({ x, points: shape.getPoints() });
      });
    });

    horizontalY.forEach((y, i) => {
      const result = sliceModelXZ(model, null, y);
      if (!result?.componentSegments) return;
      result.componentSegments.forEach((segs, j) => {
        if (segs.length < 2) return;
        const pts = buildClosedCurveWorld(
          segs,
          result.uniqueVertices,
          model.matrixWorld
        );
        if (pts.length < 3) return;
        const shape = new THREE.Shape(
          pts.map((p) => new THREE.Vector2(p.x, p.z))
        );
        const geom = new THREE.ExtrudeGeometry(shape, {
          depth: materialThickness,
          bevelEnabled: false,
        });
        const mesh = new THREE.Mesh(
          geom,
          new THREE.MeshStandardMaterial({
            ...currentMaterialRef.current,
            side: THREE.DoubleSide,
          })
        );
        mesh.rotation.x = Math.PI / 2;
        mesh.position.y = y + materialThickness / 2;
        mesh.updateMatrixWorld(true);
        sceneRef.current.add(mesh);
        horizontalPlanes.current.push(mesh);
        horizontalContours.current.push({ y, points: shape.getPoints() });
      });
    });

    const { slots } = calculateIntersections(
      horizontalContours.current,
      verticalContours.current,
      materialThickness
    );
    applySlotsToParts(
      horizontalPlanes.current,
      verticalPlanes.current,
      slots,
      materialThickness
    );
    setSvgReady(true);
    setTimeout(() => {
      checkModelBBoxConstraints();
    }, 0);

    estimateLaserTime();
  }, [materialThickness, cutNumber]);

  const checkModelBBoxConstraints = useCallback(() => {
    if (!modelRef.current) return true;

    // Get selected laser cutter bed size
    const { selected, bed } = getSelectedLaserPreset();

    // Compute model bounding box size
    const bbox = new THREE.Box3().setFromObject(modelRef.current);
    const size = bbox.getSize(new THREE.Vector3()); // { x, y, z }

    const warnings = [];

    if (size.x > bed.width || size.y > bed.height) {
      warnings.push({
        type: "size_exceeded",
        part: "Imported Model",
        printer: selected,
        currentSize: {
          width: size.x.toFixed(1),
          height: size.y.toFixed(1),
        },
        maxSize: { width: bed.width, height: bed.height },
        message: `Model size ${size.x.toFixed(1)} × ${size.y.toFixed(
          1
        )} mm exceeds bed ${bed.width} × ${bed.height} mm`,
      });
    }

    setConstraintWarnings(warnings);
    return warnings.length === 0;
  }, []); // we pull live data from window.labConstraints each call

  const exportSVG = () => {
    const combined = [
      ...horizontalContours.current.map((c) => ({ ...c, plane: "horizontal" })),
      ...verticalContours.current.map((c) => ({ ...c, plane: "vertical" })),
    ];
    downloadSVG(combined);
  };

  const initScene = useCallback(() => {
    const cleanup = initializeThreeScene(
      containerRef,
      sceneRef,
      rendererRef,
      cameraRef,
      controlsRef
    );
    sceneRef.current.add(new THREE.GridHelper(200, 20, 0x444444, 0x888888));
    sceneRef.current.add(new THREE.AxesHelper(50));
    return cleanup;
  }, []);

  const loadModel = useCallback(() => {
    setIsLoading(true);
    const loader = new STLLoader();
    const path = stlFile || "/models/default.stl";
    loader.load(
      path,
      (geometry) => {
        geometry.computeBoundingBox();
        const mesh = new THREE.Mesh(
          geometry,
          new THREE.MeshNormalMaterial({ transparent: true, opacity: 0.3 })
        );
        geometry.translate(
          -geometry.boundingBox.getCenter(new THREE.Vector3()).x,
          -geometry.boundingBox.getCenter(new THREE.Vector3()).y,
          -geometry.boundingBox.getCenter(new THREE.Vector3()).z
        );
        geometry.rotateX(-Math.PI / 2);
        geometry.rotateY(-Math.PI / 2);
        const bbox = new THREE.Box3().setFromObject(mesh);
        mesh.position.y -= bbox.min.y;
        mesh.updateMatrixWorld(true);
        modelRef.current = mesh;
        //  sceneRef.current.add(mesh);
        createVisualization(mesh, sceneRef.current, bbox);

        // 立刻做一次整体 AABB 对比
        checkModelBBoxConstraints();
        setTimeout(() => {
          generateSlices();
          setIsLoading(false);
        }, 50);
      },
      undefined,
      (err) => {
        setError("Failed to load STL");
        setIsLoading(false);
      }
    );
  }, [generateSlices]);

  useEffect(() => {
    const cleanup = initScene();
    loadModel();
    return cleanup;
  }, [initScene, loadModel]);

  useEffect(() => {
    checkModelBBoxConstraints();
  }, [laserCutterSettings.selected, checkModelBBoxConstraints]);

  return (
    <div className="container">
      <div ref={containerRef} className="canvas-container"></div>

      {constraintWarnings.length > 0 && (
        <div
          style={{
            position: "fixed",
            top: 20,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
            display: "flex",
            flexDirection: "column",
            gap: 8,
          }}
        >
          {constraintWarnings.map((w, i) => (
            <div
              key={i}
              style={{
                backgroundColor: "#ff9800",
                color: "#fff",
                padding: "12px 16px",
                borderRadius: 6,
                boxShadow: "0 2px 10px rgba(0,0,0,0.2)",
                position: "relative",
              }}
            >
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  marginBottom: 6,
                }}
              >
                <span style={{ fontSize: 16, marginRight: 8 }}>⚠️</span>
                <strong>
                  {w.plane === "horizontal" ? "Horizontal" : "Vertical"} Layer{" "}
                  {w.layer} Exceeds {w.cutter}
                </strong>
              </div>
              <div style={{ fontSize: 13 }}>
                <div>
                  Current: {w.currentSize.width} × {w.currentSize.height} mm
                </div>
                <div>
                  Max: {w.maxSize.width} × {w.maxSize.height} mm
                </div>
              </div>
              <button
                onClick={() =>
                  setConstraintWarnings((prev) =>
                    prev.filter((_, idx) => idx !== i)
                  )
                }
                style={{
                  position: "absolute",
                  top: 8,
                  right: 8,
                  background: "none",
                  border: "none",
                  color: "#fff",
                  fontSize: 16,
                  cursor: "pointer",
                }}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}

      <button
        className="panel-toggle-button"
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "⟨ Hide Panel" : "⟩ Show Panel"}
      </button>

      {!isLoading && expanded && (
        <div className="controls-panel">
          <div className="panel-title">Laser Cut Interlocking 2</div>
          <div className="panel-section">
            <div className="section-header">
              <h4>WORKFLOW DESCRIPTION</h4>
            </div>
            <div className="section-content">
              <p>
                This workflow converts your 3D model into interlocking slices
                for laser cutting. You can control the number of slices and
                material thickness to suit your fabrication needs.
              </p>
              <p style={{ fontStyle: "italic" }}>
                Recommended for flat sheet materials like plywood or cardboard.
                The interlocking structure ensures strong assembly without glue
                or screws.
              </p>
            </div>
          </div>

          <div className="panel-section">
            <div className="section-header">
              <h4>TIME ESTIMATE</h4>
            </div>
            <div className="section-content">
              <p>
                Estimated laser cut time (rough):{" "}
                <strong>{formatTimeS(estimatedCutTimeSec)}</strong>
              </p>
              <p style={{ marginTop: 6, fontSize: 13, color: "#555" }}>
                Total cut length: <strong>{formatMm(pathStats.mmTotal)}</strong>{" "}
                · Contours: <strong>{pathStats.contours}</strong>
              </p>
              <p style={{ marginTop: 6, fontSize: 12, opacity: 0.85 }}>
                Note: assumes model units are millimeters and typical plywood
                settings. Actual time varies with power/speed profiles, kerf
                compensation, and travel optimization in your cutter software.
              </p>
            </div>
          </div>

          <div
            className="section-toggle"
            onClick={() => setExpanded(!expanded)}
            style={{ cursor: "pointer" }}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 16 16"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
              style={{
                marginRight: "5px",
                transform: expanded ? "rotate(0deg)" : "rotate(-90deg)",
                transition: "transform 0.3s ease",
              }}
            >
              <path d="M8 12L2 6H14L8 12Z" fill="#333" />
            </svg>
            laser_cut_settings
          </div>

          {expanded && (
            <div className="panel-content">
              <div className="input-group">
                <label>Number of Slices</label>
                <div className="value-display">
                  <input
                    type="range"
                    className="slider"
                    min="3"
                    max="10"
                    value={cutNumber}
                    onChange={(e) => setCutNumber(parseInt(e.target.value))}
                    disabled={isLoading}
                  />
                  <div className="value">{cutNumber}</div>
                </div>
              </div>
              <div className="input-group">
                <label>Material Thickness (mm)</label>
                <div className="value-display">
                  <input
                    type="range"
                    min={thicknessMin}
                    max={thicknessMax}
                    step={thicknessStep}
                    value={materialThickness}
                    onChange={(e) =>
                      setMaterialThickness(Number(e.target.value))
                    }
                  />
                  <div className="value">{materialThickness} mm</div>
                </div>
              </div>
            </div>
          )}

          <div className="buttons-container">
            {/* 添加材料切换 */}
            <div className="material-switch">
              <button
                className={`material-button wood-btn ${
                  materialKey === "wood" ? "selected" : ""
                }`}
                onClick={() => {
                  if (materialKey !== "wood") {
                    setMaterialKey("wood");
                    applyMaterialToPlanes(woodMaterial);
                  }
                }}
                disabled={isLoading}
              >
                ● Wood
              </button>

              <button
                className={`material-button acrylic-btn ${
                  materialKey === "acrylic" ? "selected" : ""
                }`}
                onClick={() => {
                  if (materialKey !== "acrylic") {
                    setMaterialKey("acrylic");
                    applyMaterialToPlanes(acrylicMaterial);
                  }
                }}
                disabled={isLoading}
              >
                ● Acrylic
              </button>
            </div>

            <div className="material-caption">
              {materialKey === "wood" ? "Wood" : "Acrylic"}
            </div>
        {/*     <button
              onClick={exportSVG}
              className="action-button"
              disabled={!svgReady || isLoading}
            >
              DOWNLOAD SVG FILES
            </button> */}
            <button
              onClick={() => setTutorialVisible(!tutorialVisible)}
              className="action-button"
            >
              {tutorialVisible
                ? "HIDE INTERACTIVE GUIDANCE"
                : "SHOW INTERACTIVE GUIDANCE"}
            </button>
          </div>
        </div>
      )}

      {isLoading && <div className="loading-box">Loading model...</div>}
      {error && <div className="error-box">Error: {error}</div>}

      <TutorialWindow
        visible={tutorialVisible}
        onToggleVisibility={() => setTutorialVisible(!tutorialVisible)}
        tutorialContent={laserCutInterlockingTutorial}
      />
    </div>
  );
};

export default LaserCutInterlockingPlanar2;
